import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Typography, Tag, Image, Tooltip, Switch, message } from 'antd';
import dayjs from 'dayjs';
import CrudPage from '../../../components/common/CrudPage';
import { suggestionService } from '../../../services/next';
import TopicGroupsInput from './components/TopicGroupsInput';

const { Text } = Typography;

// Chuyển từ cms.next.lichviet.org/suggestion (menu "Quản lý nội dung truyền thông",
// LichVietOld\SuggestionsController). Bảng `lichviet.suggestions` + `suggestion_targets`;
// app đọc qua lichviet-api /get-suggestions. Mọi thay đổi bump cache_api 49 ở API.

const QUERY_KEY = 'suggestionList';

const STATUS_OPTIONS = [
  { label: 'Hiển thị', value: 1 },
  { label: 'Ẩn', value: 0 },
];

// Nhãn giữ nguyên form CMS cũ (0 = Âm lịch, 1 = Dương lịch). Comment cột trong DB ghi ngược
// lại, nhưng app hiện không đọc cột này — giữ đúng giá trị CMS cũ đã ghi.
const DATE_TYPE_OPTIONS = [
  { label: 'Âm lịch', value: 0 },
  { label: 'Dương lịch', value: 1 },
];

const LOOP_INFO_OPTIONS = [
  { label: 'Không lặp', value: 0 },
  { label: 'Hàng ngày', value: 1 },
  { label: 'Hàng tuần', value: 2 },
  { label: 'Hàng tháng', value: 3 },
  { label: 'Hàng năm', value: 4 },
];

const YES_NO_OPTIONS = [
  { label: 'Không', value: 0 },
  { label: 'Có', value: 1 },
];

const FALLBACK_TOPIC_GROUPS = [['lichviet_logged_in_free_users'], ['lichviet_anonymous_users']];

const DATETIME_FORMAT = 'DD/MM/YYYY HH:mm';

const parseMetadata = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
};

const MetadataCell = ({ value }) => {
  const meta = parseMetadata(value);
  if (!value) return '-';
  if (!meta) return <Text type="danger">{value}</Text>;
  const days = meta.specicalDays || {};
  const lines = [
    ['Dương', days.solar],
    ['Âm', days.lunar],
    ['Thứ', days.day_of_week],
    ['Buổi', days.time_of_day],
    ['Tập KH', meta.audiences],
  ].filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== '');
  if (lines.length === 0) {
    return (
      <Tooltip title={<pre className="m-0 whitespace-pre-wrap text-xs">{JSON.stringify(meta, null, 2)}</pre>}>
        <Text type="secondary">JSON</Text>
      </Tooltip>
    );
  }
  return (
    <div className="text-xs leading-5">
      {lines.map(([k, v]) => (
        <div key={k}>
          <Text type="secondary">{k}:</Text> {String(v)}
        </div>
      ))}
    </div>
  );
};

const validateMetadata = (_, v) => {
  if (!v || !String(v).trim()) return Promise.resolve();
  return parseMetadata(v)
    ? Promise.resolve()
    : Promise.reject(new Error('Metadata phải là JSON object hợp lệ, ví dụ {"specicalDays":{"solar":"20-10"}}'));
};

const QuanLyNoiDungTruyenThong = () => {
  const queryClient = useQueryClient();
  const [togglingId, setTogglingId] = useState(null);

  const { data: metaRes, isError: isMetaError } = useQuery({
    queryKey: ['suggestionMetadata'],
    queryFn: suggestionService.getMetadata,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (isMetaError) {
      message.warning('Không tải được danh sách Platform / Version / Topic. Tài khoản có thể thiếu quyền "Xem" của màn này.', 6);
    }
  }, [isMetaError]);

  const topicOptions = useMemo(() => metaRes?.data?.topics || { platform: [], version: [], topic: [] }, [metaRes]);
  const defaultTopicGroups = useMemo(() => {
    const groups = metaRes?.data?.defaultTopicGroups;
    return Array.isArray(groups) && groups.length > 0 ? groups : FALLBACK_TOPIC_GROUPS;
  }, [metaRes]);

  // Công tắc "Hiển thị" như danh sách CMS cũ: bật -> xuống cuối thứ tự; tắt -> đánh lại thứ tự.
  const handleToggleStatus = useCallback(
    async (record, checked) => {
      setTogglingId(record.id);
      try {
        const res = await suggestionService.changeStatus(record.id, checked ? 1 : 0);
        if (res?.code === 200) {
          message.success(res.message || 'Cập nhật trạng thái thành công');
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        } else {
          message.error(res?.message || 'Cập nhật trạng thái thất bại');
        }
      } catch (error) {
        message.error('Lỗi khi cập nhật trạng thái');
      } finally {
        setTogglingId(null);
      }
    },
    [queryClient]
  );

  const columns = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
      {
        title: 'HÌNH ẢNH',
        dataIndex: 'image_url',
        key: 'image_url',
        width: 130,
        render: (img) => (img ? <Image src={img} width={110} /> : '-'),
      },
      {
        title: 'TIÊU ĐỀ / MÔ TẢ',
        dataIndex: 'title',
        key: 'title',
        width: 220,
        render: (t, r) => (
          <div>
            {t ? <Text strong>{t}</Text> : <Text type="secondary">(không tiêu đề)</Text>}
            {r.description && (
              <div>
                <Text type="secondary" ellipsis={{ tooltip: r.description }} style={{ maxWidth: 200 }}>
                  {r.description}
                </Text>
              </div>
            )}
          </div>
        ),
      },
      { title: 'METADATA', dataIndex: 'metadata', key: 'metadata', width: 180, render: (v) => <MetadataCell value={v} /> },
      {
        title: 'LINK',
        dataIndex: 'deeplink',
        key: 'deeplink',
        width: 200,
        render: (t) => (t ? <Text ellipsis={{ tooltip: t }} style={{ maxWidth: 190 }}>{t}</Text> : '-'),
      },
      {
        title: 'TARGET',
        dataIndex: 'targetsText',
        key: 'targetsText',
        width: 220,
        render: (t) => (t ? <Text ellipsis={{ tooltip: t }} style={{ maxWidth: 210 }}>{t}</Text> : '-'),
      },
      {
        title: 'THỜI GIAN',
        dataIndex: 'end_time',
        key: 'end_time',
        width: 170,
        render: (end, r) => {
          const start = r.publish_time ? dayjs(r.publish_time).format(DATETIME_FORMAT) : '-';
          const endText = end ? dayjs(end).format(DATETIME_FORMAT) : '-';
          const expired = end && dayjs(end).isBefore(dayjs());
          return (
            <div className="text-xs leading-5">
              <div>{start}</div>
              <div>{expired ? <Tag color="red">{endText}</Tag> : endText}</div>
            </div>
          );
        },
      },
      { title: 'VỊ TRÍ', dataIndex: 'place', key: 'place', width: 70, render: (p) => (p ?? '-') },
      {
        title: 'HIỂN THỊ',
        dataIndex: 'status',
        key: 'status',
        width: 90,
        render: (s, r) => (
          <Switch
            checked={Number(s) === 1}
            loading={togglingId === r.id}
            onChange={(checked) => handleToggleStatus(r, checked)}
          />
        ),
      },
    ],
    [togglingId, handleToggleStatus]
  );

  const formFields = useCallback(
    () => [
      { type: 'divider', label: 'Target' },
      {
        name: 'platform',
        label: 'Platform',
        type: 'select',
        options: topicOptions.platform,
        span: 12,
        propsInput: { mode: 'multiple', allowClear: true, placeholder: 'Tất cả' },
      },
      {
        name: 'version',
        label: 'Version',
        type: 'select',
        options: topicOptions.version,
        span: 12,
        propsInput: { mode: 'tags', allowClear: true, placeholder: 'Tất cả (có thể nhập v9.0:v9.3 …)' },
        propsFormItem: {
          extra: 'v9.0:v9.3 = từ 9.0 đến 9.3 · v9.0:999 = từ 9.0 trở lên · v9.2 = chỉ 9.2',
        },
      },
      {
        name: 'topics',
        label: 'Topic',
        defaultValue: defaultTopicGroups,
        render: ({ value, onChange }) => (
          <TopicGroupsInput value={value} onChange={onChange} options={topicOptions.topic} />
        ),
        propsFormItem: {
          extra: 'Các topic trong cùng một nhóm là "Và"; các nhóm với nhau là "Hoặc".',
        },
      },
      { type: 'divider', label: 'Nội dung' },
      { name: 'title', label: 'Tiêu đề', type: 'textarea', span: 24, autoSize: { minRows: 2 } },
      { name: 'description', label: 'Mô tả', type: 'input', span: 24 },
      {
        name: 'image_url',
        label: 'Ảnh',
        type: 'upload-cdn',
        span: 24,
        uploadFolder: 'suggestions',
        accept: 'image/*',
        maxSizeMB: 3,
        enterLink: true,
        linkPlaceholder: 'Link ảnh',
        rules: [{ required: true, message: 'Vui lòng chọn ảnh!' }],
        propsFormItem: { extra: 'App Trang chủ (414x250)' },
      },
      { name: 'image_width', label: 'Độ rộng của ảnh', type: 'input', span: 12, rules: [{ required: true, whitespace: true }] },
      { name: 'image_height', label: 'Độ cao của ảnh', type: 'input', span: 12, rules: [{ required: true, whitespace: true }] },
      { name: 'deeplink', label: 'Deeplink', type: 'input', span: 24 },
      {
        name: 'publish_time',
        label: 'Thời gian phát hành',
        type: 'date',
        span: 12,
        format: DATETIME_FORMAT,
        propsInput: { showTime: { format: 'HH:mm' } },
        defaultValue: dayjs().second(0).millisecond(0),
        rules: [{ required: true, message: 'Vui lòng chọn thời gian phát hành!' }],
      },
      {
        name: 'end_time',
        label: 'Thời gian kết thúc',
        type: 'date',
        span: 12,
        format: DATETIME_FORMAT,
        propsInput: { showTime: { format: 'HH:mm' } },
        rules: [
          { required: true, message: 'Vui lòng chọn thời gian kết thúc!' },
          ({ getFieldValue }) => ({
            validator(_, v) {
              const start = getFieldValue('publish_time');
              if (!v || !start || !dayjs(v).isBefore(dayjs(start))) return Promise.resolve();
              return Promise.reject(new Error('Thời gian kết thúc phải sau thời gian phát hành'));
            },
          }),
        ],
      },
      { name: 'author', label: 'Tác giả', type: 'input', span: 24 },
      { name: 'content', label: 'Nội dung', type: 'ckeditor', span: 24 },
      {
        name: 'metadata',
        label: 'Metadata (JSON)',
        type: 'textarea',
        span: 24,
        placeholder: '{"specicalDays":{"solar":"20-10","lunar":"15","day_of_week":"1,2","time_of_day":"1"},"audiences":"user_pro;user_hanoi"}',
        rules: [{ validator: validateMetadata }],
        propsFormItem: {
          extra:
            'specicalDays: solar (dd-mm, "birthday", "*"), lunar, day_of_week, time_of_day — nhiều giá trị cách nhau dấu phẩy. audiences: "a,b;c" = (a và b) hoặc c. Để trống nếu không dùng.',
        },
      },
      { type: 'divider', label: 'Hiển thị' },
      { name: 'date_type', label: 'Chọn Lịch dương', type: 'select', options: DATE_TYPE_OPTIONS, defaultValue: 1, span: 8 },
      { name: 'loop_info', label: 'Tần suất lặp', type: 'select', options: LOOP_INFO_OPTIONS, defaultValue: 0, span: 8 },
      { name: 'place', label: 'Thứ tự xuất hiện', type: 'number', span: 8 },
      {
        name: 'show_on_birthday',
        label: 'Hiển thị vào đúng ngày sinh nhật',
        type: 'select',
        options: YES_NO_OPTIONS,
        defaultValue: 0,
        span: 8,
      },
      { name: 'date_from_receive', label: 'Kể từ ngày hiển thị', type: 'number', span: 8 },
      { name: 'status', label: 'Trạng thái', type: 'select', options: STATUS_OPTIONS, defaultValue: 1, span: 8 },
    ],
    [topicOptions, defaultTopicGroups]
  );

  // Danh sách đã trả sẵn platform / version / topics (nhóm) cho từng dòng -> sửa và sao chép
  // nạp thẳng từ dòng, như CMS cũ (trang copy = form tạo mới điền sẵn, giữ ảnh cũ).
  const mapDataToForm = useCallback(
    (record) => ({
      platform: record.platform || [],
      version: record.version || [],
      topics: Array.isArray(record.topics) && record.topics.length > 0 ? record.topics : [[]],
    }),
    []
  );

  const mapFormToData = useCallback(
    (payload) => ({
      topics: (payload.topics || []).filter((g) => Array.isArray(g) && g.length > 0),
      metadata: payload.metadata ? String(payload.metadata).trim() : null,
    }),
    []
  );

  const filterFields = useMemo(
    () => [
      { label: 'Trạng thái', value: 'status', options: [{ label: 'Tất cả', value: '' }, ...STATUS_OPTIONS] },
      { label: 'Phát hành từ ngày', value: 'publish_from', type: 'date', submitFormat: 'YYYY-MM-DD' },
      { label: 'Kết thúc đến ngày', value: 'end_to', type: 'date', submitFormat: 'YYYY-MM-DD' },
    ],
    []
  );

  return (
    <CrudPage
      title="NỘI DUNG TRUYỀN THÔNG"
      subTitle="Gợi ý hiển thị trên app (lichviet-api /get-suggestions): sắp theo Vị trí, lọc theo Target, thời gian và metadata"
      itemsName="Nội dung"
      queryKey={QUERY_KEY}
      service={suggestionService}
      columns={columns}
      formFields={formFields}
      filterFields={filterFields}
      searchPlaceholder="Tìm theo tiêu đề hoặc mô tả..."
      addButtonLabel="Tạo mới"
      deleteTitle="Xóa nội dung này?"
      deleteDesc="Xoá vĩnh viễn như CMS cũ, không khôi phục được."
      formConfig={{ width: 900 }}
      mapDataToForm={mapDataToForm}
      mapFormToData={mapFormToData}
    />
  );
};

export default QuanLyNoiDungTruyenThong;
